import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ApiCallingService } from "../../services/api-calling.service";
import { ConfigApi } from "../../config-api";
import { ToastrService } from "ngx-toastr";
import { Globals } from "../../common/global";
import { HttpParams } from "@angular/common/http";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { PosOrderModalComponent } from "../pos-order-modal/pos-order-modal.component";

@Component({
  selector: "app-pos-track-order",
  templateUrl: "./pos-track-order.component.html",
  styleUrls: ["./pos-track-order.component.css"],
})
export class PosTrackOrderComponent implements OnInit {
  orders: any[] = [];
  page: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  loading: boolean = false;
  searchQuery: string = "";
  private searchDebounceTimer: any;
  statusList = [
    "all",
    "pending",
    "accepted",
    "completed",
    "cancelled",
    "readyToPickup",
  ];
  getStatusLabel(status: string): string {
    if (!status) return "";

    return status
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase());
  }
  selectedStatus = "all";
  private statusDebounceTimer: any;
  statusLabelMap: Record<string, string> = {
    pending: "Pending",
    accepted: "Accepted",
    completed: "Completed",
    cancelled: "Cancelled",
    readyToPickup: "Ready to Pickup",
  };
  constructor(
    private router: Router,
    private apiCallingService: ApiCallingService,
    private toastr: ToastrService,
    public globals: Globals,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    if (!this.globals.user) {
      this.globals.getUser();
    }
    this.getOrders();
  }

  getOrders(): void {
    this.loading = true;
    const user = this.globals.user || this.globals.getUser();
    const user_id = user ? user._id : "";
    const pageIndex = this.page > 0 ? this.page - 1 : 0;

    let params = new HttpParams()
      .set("operator_id", user_id)
      .set("page", pageIndex.toString())
      .set("limit", this.pageSize.toString());

    if (this.searchQuery) {
      params = params.set("search", this.searchQuery);
    }

    if (this.selectedStatus && this.selectedStatus !== "all") {
      params = params.set("status", this.selectedStatus);
    }

    this.apiCallingService
      .callAPI(
        {},
        "GET",
        `${ConfigApi.URLS.POS.LIST_ORDER}?${params.toString()}`,
      )
      .subscribe(
        (res) => {
          console.log("orders", res);

          this.loading = false;
          if (this.apiCallingService.handleATZSuccess(res)) {
            this.orders = res.data;
            this.totalCount = res.pagination.total_counts;
          }
        },
        (error) => {
          this.loading = false;
          this.toastr.error("Failed to load orders");
        },
      );
  }

  onSearchChange() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.page = 1;
      this.getOrders();
    }, 500);
  }

  onStatusChange() {
    if (this.statusDebounceTimer) {
      clearTimeout(this.statusDebounceTimer);
    }
    this.statusDebounceTimer = setTimeout(() => {
      this.page = 1;
      this.getOrders();
    }, 300);
  }

  loadPage(page: number): void {
    if (page) {
      this.page = page;
      this.getOrders();
    }
  }

  viewOrderDetails(order: any): void {
    this.router.navigate(["/user/pos-orders", order._id]);
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case "pending":
        return "badge-warning";
      case "accepted":
        return "badge-info";
      case "readytopickup":
        return "badge-primary";
      case "completed":
        return "badge-success";
      case "cancelled":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  }

  getAvailableActions(order: any): string[] {
    if (!order || !order.status) {
      return [];
    }

    const status = order.status;
    const actions = [];

    if (status === "pending") {
      actions.push("accept");
    } else if (status === "accepted") {
      actions.push("readyForPickup");
    } else if (status === "readyToPickup") {
      actions.push("complete");
    }

    if (["pending", "accepted", "readyToPickup"].includes(status)) {
      actions.push("cancel");
    }

    return actions;
  }

  openModal(actionType: string, order: any): void {
    const modalRef = this.modalService.open(PosOrderModalComponent, {
      centered: true,
      backdrop: "static",
    });

    const titleMap: { [key: string]: string } = {
      accept: "Accept Order",
      readyForPickup: "Ready for Pickup",
      complete: "Complete Order",
      cancel: "Cancel Order",
    };

    modalRef.componentInstance.title = titleMap[actionType] || "Order Action";
    modalRef.componentInstance.actionType = actionType;
    modalRef.componentInstance.order = order;
    modalRef.componentInstance.requiresReason = actionType === "cancel";

    modalRef.result.then(
      (result) => {
        if (result) {
          if (actionType === "cancel") {
            this.performAction("cancelled", order._id, result);
          } else {
            const nextStatusMap: { [key: string]: string } = {
              accept: "accepted",
              readyForPickup: "readyToPickup",
              complete: "completed",
            };
            this.performAction(nextStatusMap[actionType], order._id);
          }
        }
      },
      () => {},
    );
  }

  performAction(status: string, orderId: string, reason?: string): void {
    const payload: any = { status };
    if (status === "cancelled" && reason) {
      payload.cancel_reason = reason;
    }

    this.loading = true;
    this.apiCallingService
      .callAPI(
        payload,
        "PUT",
        `${ConfigApi.URLS.POS.UPDATE_ORDER_STATUS}${orderId}`,
      )
      .subscribe(
        (res) => {
          this.loading = false;
          if (this.apiCallingService.handleATZSuccess(res)) {
            this.toastr.success(`Order status updated to ${status}`);
            this.getOrders();
          }
        },
        (error) => {
          this.loading = false;
          this.toastr.error("Failed to update order status");
        },
      );
  }
}
